import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { applySeo } from './lib/seo';
import { trackPageView, trackContact } from './lib/metaPixel';
import { trackOaiqPageView } from './lib/openaiPixel';
import { LanguageProvider } from './context/LanguageContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import Header from './components/Header';
import Hero from './components/Hero';
import AboutSection from './components/AboutSection';
import PortfolioSection from './components/PortfolioSection';
import ContactSection from './components/ContactSection';
import Footer from './components/Footer';
import ThankYouPage from './pages/ThankYouPage';
import AgendaPage from './pages/AgendaPage';
import BlogPage from './pages/BlogPage';
import OSLogin from './pages/OSLogin';
import OSLayout from './layouts/OSLayout';
import OSDashboard from './pages/os/OSDashboard';
import OSProjects from './pages/os/OSProjects';
import OSProjectDetail from './pages/os/OSProjectDetail';
import OSFurnitureDetail from './pages/os/OSFurnitureDetail';
import OSIntake from './pages/os/OSIntake';
import NosotrosPage from './pages/NosotrosPage';
import ProyectosPage from './pages/ProyectosPage';
import ContactoPage from './pages/ContactoPage';
import NotFoundPage from './pages/NotFoundPage';
import PoliticaReembolsoPage from './pages/PoliticaReembolsoPage';
import OSTasks from './pages/os/OSTasks';
import OSTeam from './pages/os/OSTeam';

function WhatsAppButton() {
  return (
    <a
      href="https://wa.me/529841793706"
      onClick={() => trackContact()}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="whatsapp-button"
      className="fixed bottom-[30px] right-[30px] w-[60px] h-[60px] bg-[#25D366] rounded-full flex items-center justify-center z-[9999] transition-all duration-300 hover:scale-110"
      style={{ boxShadow: '0 8px 20px rgba(0,0,0,0.25)' }}
    >
      <svg viewBox="0 0 32 32" className="w-8 h-8" fill="#fff" aria-label="WhatsApp" role="img">
        <path d="M16.004 3.2c-7.06 0-12.8 5.74-12.8 12.8 0 2.26.59 4.46 1.71 6.4L3.2 28.8l6.58-1.67a12.74 12.74 0 0 0 6.22 1.61h.01c7.06 0 12.8-5.74 12.8-12.8s-5.74-12.74-12.81-12.74zm0 23.4h-.01a10.6 10.6 0 0 1-5.4-1.48l-.39-.23-3.9.99 1.04-3.8-.25-.4a10.58 10.58 0 0 1-1.63-5.68c0-5.87 4.78-10.64 10.66-10.64a10.6 10.6 0 0 1 10.64 10.66c0 5.87-4.78 10.64-10.65 10.64zm5.84-7.97c-.32-.16-1.89-.93-2.19-1.04-.29-.11-.51-.16-.72.16-.21.32-.83 1.04-1.01 1.25-.19.21-.37.24-.69.08-.32-.16-1.35-.5-2.57-1.59-.95-.85-1.59-1.9-1.78-2.22-.19-.32-.02-.49.14-.65.14-.14.32-.37.48-.56.16-.19.21-.32.32-.53.11-.21.05-.4-.03-.56-.08-.16-.72-1.73-.98-2.37-.26-.62-.52-.54-.72-.55h-.61c-.21 0-.56.08-.85.4-.29.32-1.12 1.09-1.12 2.66s1.15 3.09 1.31 3.3c.16.21 2.26 3.45 5.47 4.84.76.33 1.36.53 1.82.67.77.24 1.47.21 2.02.13.62-.09 1.89-.77 2.16-1.52.27-.75.27-1.39.19-1.52-.08-.13-.29-.21-.61-.37z"/>
      </svg>
    </a>
  );
}

// PageView en cambios de ruta internos (el PageView inicial lo dispara el snippet de index.html)
function MetaPixelRouteTracker() {
  const location = useLocation();
  const firstLoad = React.useRef(true);

  React.useEffect(() => {
    applySeo(location.pathname);
    // OpenAI Pixel: page_viewed en carga inicial y cada cambio de ruta (dedupe interno)
    trackOaiqPageView(location.pathname);
    if (firstLoad.current) { firstLoad.current = false; return; }
    trackPageView();
  }, [location.pathname]);

  return null;
}

function HomePage() {
  const location = useLocation();


  return (
    <>
      <Header />
      <main>
        <Hero />
        <AboutSection />
        <PortfolioSection />
        <ContactSection />
      </main>
      <Footer />
      <WhatsAppButton />
    </>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF9F6' }}>
      <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#C9A66B', borderTopColor: 'transparent' }} />
    </div>
  );
  if (!user) return <Navigate to="/os/login" replace />;
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <MetaPixelRouteTracker />
      <LanguageProvider>
        <AuthProvider>
          <div className="App">
            <Routes>
              {/* Landing page */}
              <Route path="/" element={<HomePage />} />
              <Route path="/nosotros" element={<NosotrosPage />} />
              <Route path="/proyectos" element={<ProyectosPage />} />
              <Route path="/contacto" element={<ContactoPage />} />
              <Route path="/politica-de-reembolso" element={<PoliticaReembolsoPage />} />
              <Route path="/respuesta" element={<ThankYouPage />} />
              <Route path="/agenda" element={<AgendaPage />} />
              <Route path="/blog" element={<BlogPage />} />

              {/* OS Auth */}
              <Route path="/os" element={<Navigate to="/os/login" replace />} />
              <Route path="/os/login" element={<OSLogin />} />

              {/* OS App (Protected) */}
              <Route path="/os" element={<ProtectedRoute><OSLayout /></ProtectedRoute>}>
                <Route path="dashboard" element={<OSDashboard />} />
                <Route path="intake" element={<OSIntake />} />
                <Route path="projects" element={<OSProjects />} />
                <Route path="projects/:id" element={<OSProjectDetail />} />
                <Route path="projects/:id/furniture/:fid" element={<OSFurnitureDetail />} />
                <Route path="tasks" element={<OSTasks />} />
                <Route path="team" element={<OSTeam />} />
              </Route>
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            <Toaster position="top-center" richColors />
          </div>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
